import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { InputTheme, InputVariant } from '@presentation/components';
import { mediaQuery } from '@presentation/styles';

export const Wrapper = styled.div<{ inputTheme: InputTheme; hasDropdown: boolean }>`
  position: relative;
  height: fit-content;
  width: 100%;
  block-size: fit-content;

  ${(props) => props.hasDropdown && 'padding-bottom: 10px; margin-bottom: -10px;'}

  // &:focus-within [data-dropdown] {
  //   display: block;
  // }

  &:focus-within > input {
    ${(props) =>
      props.inputTheme === 'light' &&
      `
    border-color: ${props.theme.color.blue.default};
    `}
  }

  [data-dropdown-icon] {
    transition: transform 0.3s ease;
  }

  &:focus-within [data-dropdown-icon] {
    transform: scaleY(-1);
  }
`;

export const IconWrapper = styled.div<{
  hasDropdown: boolean;
  position: 'right' | 'left';
  textarea: boolean;
  inputTheme: InputTheme;
}>`
  width: 24px;
  height: 24px;
  position: absolute;
  z-index: 1;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${(props) =>
    props.inputTheme === 'light' ? props.theme.color.dark.default : props.theme.color.light.tint};

  ${(props) => props.position === 'right' && 'right: 12px;'}
  ${(props) => props.position === 'left' && 'left: 12px;'}

  ${(props) => (props.textarea ? 'top: 12px;' : 'top: 50%; transform: translateY(-50%);')}
  ${(props) => props.hasDropdown && !props.textarea && 'top: calc(50% - 5px);'}
`;

export const inputStyled = (
  theme: Theme,
  hasIconLeft: boolean,
  hasIconRight: boolean,
  inputTheme: InputTheme,
  disabled: boolean,
  visuallyDisabled: boolean,
  hasError: boolean,
  hideNumberArrows: boolean,
  variant: InputVariant,
) => css`
  width: 100%;
  padding: 7px 12px;
  font-size: 16px;
  border-radius: ${variant === 'new' ? '8px' : '10px'};

  border: 2px solid ${inputTheme === 'light' ? theme.color.light.shade : theme.color.light.default};
  color: ${inputTheme === 'light'
    ? theme.color.dark.default
    : theme.color.light.default} !important;

  -webkit-text-fill-color: ${inputTheme === 'light'
    ? theme.color.dark.default
    : theme.color.light.default} !important;

  background: ${inputTheme === 'light' ? theme.color.light.default : theme.color.tranparent};

  ${hasError && `border-color: ${theme.color.red.default} !important;`}

  &::placeholder {
    font-style: normal;
    color: ${inputTheme === 'light'
      ? theme.color.medium.default
      : theme.color.light.tint} !important;

    -webkit-text-fill-color: ${inputTheme === 'light'
      ? theme.color.medium.default
      : theme.color.light.tint} !important;
  }

  ${disabled &&
  `
    pointer-events: none;
  `}

  ${visuallyDisabled &&
  `
    cursor: not-allowed;
    background: ${
      inputTheme === 'light' ? theme.color.light.shadeLighter : theme.color.light.shadeLighter
    };
  `}

  ${hasIconLeft &&
  `
    padding-left: 42px; // 12 + 24 + 6
  `}
  ${hasIconRight &&
  `
    padding-right: 42px; // 12 + 24 + 6
  `}

  ${hideNumberArrows &&
  `
    -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  `}
`;

export const Input = styled.input<{
  hasIconLeft: boolean;
  hasIconRight: boolean;
  inputTheme: InputTheme;
  disabled: boolean;
  visuallyDisabled: boolean;
  hasError: boolean;
  hideNumberArrows: boolean;
  variant: InputVariant;
}>`
  ${(props) =>
    inputStyled(
      props.theme,
      props.hasIconLeft,
      props.hasIconRight,
      props.inputTheme,
      props.disabled,
      props.visuallyDisabled,
      props.hasError,
      props.hideNumberArrows,
      props.variant,
    )};
`;

export const Textarea = styled.textarea<{
  hasIconLeft: boolean;
  hasIconRight: boolean;
  inputTheme: InputTheme;
  disabled: boolean;
  visuallyDisabled: boolean;
  hasError: boolean;
  hideNumberArrows: boolean;
  variant: InputVariant;
}>`
  ${(props) =>
    inputStyled(
      props.theme,
      props.hasIconLeft,
      props.hasIconRight,
      props.inputTheme,
      props.disabled,
      props.visuallyDisabled,
      props.hasError,
      props.hideNumberArrows,
      props.variant,
    )}
  resize: vertical;
  min-height: 100px;
`;

export const DropdownWrapper = styled.div<{
  mobileStyleDropdown: boolean;
  relativeDropdown: boolean;
  variant: InputVariant;
}>`
  overflow: hidden;
  display: none;

  transition: height 0ms 300ms, opacity 300ms 0ms;

  background: ${(props) => props.theme.color.light.default};
  color: ${(props) => props.theme.color.dark.default};

  box-shadow: 0 0 7px 0 rgba(0, 0, 0, 0.15);
  border-radius: ${(props) => (props.variant === 'new' ? '8px' : '10px')};
  min-width: 200px;
  width: 100%;

  ${(props) =>
    props.relativeDropdown
      ? `
  margin-top: 10px;
  `
      : `
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10000000;
  `}

  ${mediaQuery('phone')} {
    ${(props) =>
      props.mobileStyleDropdown &&
      `
    top: unset;
    bottom: 0;
    position: fixed;
    border-radius: 0;
    `}
  }
`;

export const MobileDropdownControlWrapper = styled.div`
  ${mediaQuery('phone', 'min')} {
    display: none;
  }
  background: ${(props) => props.theme.color.light.tint};
  padding: 5px 20px;
  display: flex;
  border-top: 2px solid ${(props) => props.theme.color.light.shade};
  border-bottom: 2px solid ${(props) => props.theme.color.light.shade};
`;

export const MobileDropdownCloseButton = styled.div`
  margin-left: auto;
  color: ${(props) => props.theme.color.blue.default};
  font-weight: 700;
  cursor: pointer;
`;

export const HelperText = styled.div`
  color: ${(props) => props.theme.color.medium.default};
  margin-left: 6px;
  margin-top: 6px;
  font-size: 12px;
`;
